/**
 * Bulk product import: pick a format, download the template, upload, review, apply.
 *
 * Preview-then-confirm rather than fire-and-forget, the same way the CRM credit
 * upload works. A supplier catalogue is hundreds of rows and "921 products
 * imported" is not something anyone can verify after the fact -- the review step
 * is where a wrong column or a stale file gets caught while it still costs
 * nothing.
 *
 * Any unreadable or ambiguous row refuses the WHOLE file. Importing the readable
 * 900 and listing 23 failures leaves nobody able to say what the catalogue now
 * contains.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload, faFileCsv, faTimes, faTriangleExclamation, faUpload,
} from '@fortawesome/free-solid-svg-icons';

import { stockService } from '../../services/stockService';
import './ProductCsvImportModal.css';

const ACTION_LABELS = {
    create: 'New',
    update: 'Update',
    unchanged: 'No change',
    duplicate: 'Duplicate row',
};

const ProductCsvImportModal = ({ isOpen, onClose, onImported, initialFormat }) => {
    const [formats, setFormats] = useState([]);
    // Seeded here for the first paint; ``initialFormat`` is re-applied on every
    // open (see the effect below) because the tab can change underneath.
    const [formatKey, setFormatKey] = useState(initialFormat || '');
    const [file, setFile] = useState(null);
    const [plan, setPlan] = useState(null);
    const [errors, setErrors] = useState([]);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);

    const reset = useCallback(() => {
        setFile(null);
        setPlan(null);
        setErrors([]);
        setMessage('');
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        (async () => {
            try {
                const rows = await stockService.listCsvFormats();
                if (cancelled) return;
                setFormats(rows);
                // Follow the tab the user opened this FROM, every time. The modal
                // mounts with the page (so its first `initialFormat` is whatever
                // tab was active at load) and seeding state once left someone
                // standing on Lubrication being offered the bulk-commodity
                // columns -- a wall of row errors for picking nothing wrong.
                setFormatKey(
                    rows.some((r) => r.key === initialFormat)
                        ? initialFormat
                        : (rows[0]?.key || ''),
                );
            } catch (e) {
                if (!cancelled) setMessage('Could not load the upload formats.');
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, initialFormat]);

    const activeFormat = useMemo(
        () => formats.find((f) => f.key === formatKey) || null,
        [formats, formatKey],
    );

    const handleDownloadTemplate = async () => {
        try {
            const blob = await stockService.downloadCsvTemplate(formatKey);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `imt-products-${formatKey}-template.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setMessage('Could not download the template.');
        }
    };

    const run = async (preview) => {
        if (!file || !formatKey) return;
        setBusy(true);
        setErrors([]);
        setMessage('');
        try {
            const data = await stockService.importProductsCsv(file, formatKey, { preview });
            setPlan(preview ? data : null);
            setMessage(data.message || '');
            if (!preview) {
                onImported?.(data);
            }
        } catch (e) {
            const body = e?.response?.data || {};
            setErrors(body.errors || []);
            setMessage(body.message || body.error || 'The upload failed.');
            setPlan(null);
        } finally {
            setBusy(false);
        }
    };

    if (!isOpen) return null;

    const summary = plan?.summary;

    return (
        <div className="pci-overlay" role="dialog" aria-modal="true" aria-label="Import products from CSV">
            <div className="pci-modal">
                <div className="pci-header">
                    <h2><FontAwesomeIcon icon={faFileCsv} /> Import products from CSV</h2>
                    <button type="button" className="pci-close" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="pci-body">
                    <label className="pci-label" htmlFor="pci-format">What are you uploading?</label>
                    <select
                        id="pci-format"
                        className="pci-select"
                        value={formatKey}
                        onChange={(e) => { setFormatKey(e.target.value); reset(); }}
                        disabled={busy}
                    >
                        {formats.map((f) => (
                            <option key={f.key} value={f.key}>{f.label}</option>
                        ))}
                    </select>
                    {activeFormat && (
                        <p className="pci-hint">{activeFormat.description}</p>
                    )}

                    {activeFormat && (
                        <div className="pci-columns">
                            <span className="pci-columns-title">Columns</span>
                            {activeFormat.columns.map((c) => (
                                <span key={c.header} className={`pci-chip ${c.required ? 'required' : ''}`}>
                                    {c.header}{c.required ? ' *' : ''}
                                </span>
                            ))}
                        </div>
                    )}

                    <button
                        type="button"
                        className="pci-btn pci-btn-secondary"
                        onClick={handleDownloadTemplate}
                        disabled={!formatKey || busy}
                    >
                        <FontAwesomeIcon icon={faDownload} /> Download template
                    </button>

                    <label className="pci-label" htmlFor="pci-file">CSV file</label>
                    <input
                        id="pci-file"
                        type="file"
                        accept=".csv,text/csv"
                        className="pci-file"
                        onChange={(e) => { setFile(e.target.files?.[0] || null); setPlan(null); setErrors([]); }}
                        disabled={busy}
                    />

                    {/* Refusals list every offending row: "fix the file" is only
                      * actionable if the user is told which lines to fix. */}
                    {errors.length > 0 && (
                        <div className="pci-errors">
                            <div className="pci-errors-head">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                Nothing was imported. {errors.length} row(s) need fixing:
                            </div>
                            <ul>
                                {errors.slice(0, 25).map((err, i) => (
                                    <li key={`${err.row}-${i}`}>Row {err.row}: {err.error}</li>
                                ))}
                            </ul>
                            {errors.length > 25 && (
                                <p className="pci-hint">…and {errors.length - 25} more.</p>
                            )}
                        </div>
                    )}

                    {message && errors.length === 0 && (
                        <p className="pci-message">{message}</p>
                    )}

                    {summary && (
                        <div className="pci-summary">
                            <span className="pci-stat"><strong>{summary.create}</strong> new</span>
                            <span className="pci-stat"><strong>{summary.update}</strong> updated</span>
                            <span className="pci-stat"><strong>{summary.unchanged}</strong> unchanged</span>
                            <span className="pci-stat"><strong>{summary.duplicate}</strong> duplicate rows skipped</span>
                        </div>
                    )}

                    {plan?.plan?.length > 0 && (
                        <div className="pci-table-wrap">
                            <table className="pci-table">
                                <thead>
                                    <tr>
                                        <th>Row</th>
                                        <th>Product</th>
                                        <th>What happens</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {plan.plan.map((r) => (
                                        <tr key={r.row} className={`pci-row-${r.action}`}>
                                            <td>{r.row}</td>
                                            <td>{r.identity}</td>
                                            <td>
                                                {ACTION_LABELS[r.action] || r.action}
                                                {r.reason ? ` — ${r.reason}` : ''}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {plan.plan_truncated && (
                                <p className="pci-hint">
                                    Showing the first 500 rows. Every row in the file will be imported.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="pci-footer">
                    <button type="button" className="pci-btn pci-btn-secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="pci-btn pci-btn-secondary"
                        onClick={() => run(true)}
                        disabled={!file || !formatKey || busy}
                    >
                        <FontAwesomeIcon icon={faUpload} /> Review
                    </button>
                    <button
                        type="button"
                        className="pci-btn pci-btn-primary"
                        onClick={() => run(false)}
                        disabled={!plan || busy}
                        title={!plan ? 'Review the file first' : undefined}
                    >
                        Import {summary ? `${summary.create + summary.update} product(s)` : ''}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductCsvImportModal;
